import React from 'react';

const LogFilters = ({ 
  filters, 
  onFilterChange, 
  onClearFilters,
  users = [],
  loading = false 
}) => {
  const actionOptions = [
    { value: '', label: 'All Actions (Excluding System)' },
    { value: 'all', label: 'All Actions (Including System)' },
    { value: 'CREATE', label: 'Create' },
    { value: 'UPDATE', label: 'Update' },
    { value: 'DELETE', label: 'Delete' },
    { value: 'VIEW', label: 'View' },
    { value: 'EXPORT', label: 'Export' },
    { value: 'BULK_UPDATE', label: 'Bulk Update' },
    { value: 'REALTIME_SYNC', label: 'System Sync' },
    { value: 'TEST', label: 'Test Logs' }
  ];

  const resourceOptions = [
    { value: '', label: 'All Resources' },
    { value: 'sales', label: 'Sales' },
    { value: 'users', label: 'Users' },
    { value: 'system', label: 'System' },
    { value: 'products', label: 'Products' },
    { value: 'clients', label: 'Clients' }
  ];

  const severityOptions = [
    { value: '', label: 'All Severities' },
    { value: 'LOW', label: 'Low' },
    { value: 'MEDIUM', label: 'Medium' },
    { value: 'HIGH', label: 'High' },
    { value: 'CRITICAL', label: 'Critical' }
  ];

  // eslint-disable-next-line no-unused-vars
  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'LOW': return 'text-green-600';
      case 'MEDIUM': return 'text-yellow-600';
      case 'HIGH': return 'text-orange-600';
      case 'CRITICAL': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  return (
    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6">
      <div className="flex flex-col lg:flex-row gap-4 items-end">
        {/* Action Filter */}
        <div className="flex-1">
          <label htmlFor="action-filter" className="block text-sm font-medium text-gray-700 mb-1">
            Action
          </label>
          <select
            id="action-filter"
            value={filters.action || ''}
            onChange={(e) => onFilterChange('action', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={loading}
          >
            {actionOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Resource Filter */}
        <div className="flex-1">
          <label htmlFor="resource-filter" className="block text-sm font-medium text-gray-700 mb-1">
            Resource
          </label>
          <select
            id="resource-filter"
            value={filters.entityType || ''}
            onChange={(e) => onFilterChange('entityType', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={loading}
          >
            {resourceOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* User Filter */}
        <div className="flex-1">
          <label htmlFor="user-filter" className="block text-sm font-medium text-gray-700 mb-1">
            User
          </label>
          <select
            id="user-filter"
            value={filters.userId || ''}
            onChange={(e) => onFilterChange('userId', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={loading}
          >
            <option value="">All Users</option>
            {users.map(user => (
              <option key={user.id} value={user.id}>
                {user.email || user.displayName || user.id}
              </option>
            ))}
          </select>
        </div>

        {/* Severity Filter */}
        <div className="flex-1">
          <label htmlFor="severity-filter" className="block text-sm font-medium text-gray-700 mb-1">
            Severity
          </label>
          <select
            id="severity-filter"
            value={filters.severity || ''}
            onChange={(e) => onFilterChange('severity', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={loading}
          >
            {severityOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Date Range Filter */}
        <div className="flex-1">
          <label htmlFor="date-from" className="block text-sm font-medium text-gray-700 mb-1">
            Date From
          </label>
          <input
            type="date"
            id="date-from"
            value={filters.dateFrom || ''}
            onChange={(e) => onFilterChange('dateFrom', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={loading}
          />
        </div>

        <div className="flex-1">
          <label htmlFor="date-to" className="block text-sm font-medium text-gray-700 mb-1">
            Date To
          </label>
          <input
            type="date"
            id="date-to"
            value={filters.dateTo || ''}
            onChange={(e) => onFilterChange('dateTo', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={loading}
          />
        </div>

        {/* Clear Filters Button */}
        <div className="flex-shrink-0">
          <button
            onClick={onClearFilters}
            disabled={loading}
            className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Active Filters Display */}
      {Object.keys(filters).some(key => filters[key]) && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex flex-wrap gap-2">
            <span className="text-sm text-gray-600">Active filters:</span>
            {Object.entries(filters).map(([key, value]) => {
              if (!value) return null;
              
              let displayValue = value;
              if (key === 'action') {
                displayValue = actionOptions.find(opt => opt.value === value)?.label || value;
              } else if (key === 'entityType') {
                displayValue = resourceOptions.find(opt => opt.value === value)?.label || value;
              } else if (key === 'severity') {
                displayValue = severityOptions.find(opt => opt.value === value)?.label || value;
              } else if (key === 'userId') {
                const user = users.find(u => u.id === value);
                displayValue = user?.email || user?.displayName || value;
              }
              
              return (
                <span
                  key={key}
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                >
                  {key}: {displayValue}
                  <button
                    onClick={() => onFilterChange(key, '')}
                    className="ml-1 text-blue-600 hover:text-blue-800"
                  >
                    ×
                  </button>
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default LogFilters;
